<script>
  import { onMount, getContext } from "svelte";
  import cloneDeep from "lodash/cloneDeep";
  import set from "lodash/set";
  import { Modal } from "svelte-utilities";
  import eb from "#src/extensions/event-bus.js";
  import SelectInput from "#lib/sidebar/SelectInput.svelte";
  import BooleanInput from "#lib/sidebar/BooleanInput.svelte";
  import Datatable from "../datatable/Datatable.svelte";
  import { profile } from "#src/stores/auth.js";
  import { sortableList } from "@local/extensions/collections/sortable-list.js";

  export let group;
  export let standardColumns;

  const org = getContext("org");

  let modal;

  const standardColumnProps = [
    {
      label: "Name",
      prop: "label",
      editable: false,
    },
    {
      label: "Name override",
      prop: "override",
      editable: true,
    },
    {
      label: "Visible",
      prop: "visible",
      editable: true,
      type: "boolean",
    },
  ];

  const standardCols = (hasTemplates = false) => [
    { label: "Mark", id: "mark" },
    { label: "Width", id: "width" },
    { label: "Height", id: "height" },
    { label: "Type", id: "type_id" },
    { label: "Description", id: "description" },
    { label: "Quantity", id: "quantity" },
    { label: "Est. Area", id: "cache.area" },
    { label: "Est. Wt.", id: "cache.weight" },
    { label: "Notes", id: "notes" },
    { label: "Columns", id: "x_subdivisions" },
    { label: "Rows", id: "y_subdivisions" },
    { label: "Scope", id: "scope" },
    { label: "Price (each)", id: "price" },
    { label: "Price", id: "price_total" },
    ...(hasTemplates ? [{ label: "Item Template", id: "item_template" }] : []),
  ];

  $: sc = getStandardColumns(standardColumns, $org);
  $: canAdmin = $profile?.profile_capabilities?.capabilities?.edit_basic_org_settings;

  function openModal() {
    modal.open();
  }

  function updateSetting(setting, value) {
    group.updateProp(`data.settings.${setting}`, value);
  }

  function getStandardColumns(standardColumns, org) {
    const hasTemplates = !!org?.data?.templates?.length;

    return standardCols(hasTemplates).map((c) => {
      const visible = ["x_subdivisions", "y_subdivisions"].includes(c.id) ? false : true; // Hide subdivisions by default

      return (
        standardColumns[c.id] || {
          ...c,
          override: null,
          visible,
        }
      );
    });
  }

  function updateStandardColumn(e) {
    const { id, prop, value } = e.detail;
    const data = cloneDeep($org.data);
    const newSc = sortableList(sc);
    set(newSc[id], prop, value);
    data.standard_columns = newSc;
    org.updateProp("data", data);
    group.react();
  }

  onMount(() => {
    eb.on("open-home-settings-modal", openModal);

    return () => {
      eb.unsubscribe("open-home-settings-modal", openModal);
    };
  });
</script>

<Modal bind:this={modal} closeable fullframe width="36rem" height="26rem">
  <div slot="title">Settings</div>
  <div slot="content" class="text-xs h-full overflow-auto p-4 space-y-2">
    <div class="flex items-center justify-between">
      <div>Default Type View</div>
      <div class="flex-none w-56">
        <SelectInput
          options={[
            { label: "Show Type Details", value: -1 },
            { label: "Show Type Details and Items", value: 1 },
            { label: "Show Type Name Only", value: 0 },
          ]}
          value={$group.data.settings?.summary_default_type_view}
          label={null}
          on:input={(e) => updateSetting("summary_default_type_view", e.detail.value)} />
      </div>
    </div>
    <div class="flex items-center justify-between">
      <div>Show Untyped Items</div>
      <div class="flex-none w-56">
        <BooleanInput
          value={$group.data.settings?.summary_show_untyped_items}
          label={null}
          on:input={(e) => updateSetting("summary_show_untyped_items", e.detail.value)} />
      </div>
    </div>
    <div class="py-2">
      <div class="mb-2">Standard Columns</div>
      <div class="data-table-container">
        <Datatable
          sticky
          size="xxs"
          data={sc}
          columns={standardColumnProps}
          editable={canAdmin}
          on:updaterow={updateStandardColumn} />
      </div>
    </div>
  </div>
</Modal>

<style lang="scss">
  .data-table-container {
    @apply border overflow-y-auto relative;
    max-height: 14rem;
  }
</style>
